import React, { useState, useRef, useCallback, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  MapPin,
  Image as ImageIcon,
  Plus,
  X,
  Loader2,
  AlertCircle,
  CheckCircle2,
  UploadCloud,
  Pencil,
  Trash2,
  ArrowLeft,
  PackagePlus,
  Star,
  BedDouble,
  Copy,
  GripVertical,
  CalendarIcon,
} from "lucide-react";
import { format } from "date-fns";
import { Calendar } from "@/components/ui/calender";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { useDragReorder } from "@/lib/useDragReorder";
import { saveItemOrder } from "@/services/reorderService";
import MatrixPriceEditor, {
  ACCOMMODATION_DAY_TYPES,
} from "@/components/admin/Matrixpriceeditor";
import { IMAGE_ACCEPT, isImageFile } from "@/lib/uploadImage";

function validateAccommodationForm(form) {
  const errors = {};

  const title = form.title.trim();
  if (!title) errors.title = "Hotel/resort name is required.";
  else if (title.length < 3)
    errors.title = "Name must be at least 3 characters.";
  else if (title.length > 120)
    errors.title = "Name must be under 120 characters.";

  if (!form.destination.trim()) errors.destination = "Area is required.";

  const price = Number(form.price);
  if (form.price === "") errors.price = "Price per night is required.";
  else if (Number.isNaN(price)) errors.price = "Price must be a number.";
  else if (price <= 0) errors.price = "Price must be greater than 0.";
  else if (price > 200000)
    errors.price =
      "Price looks wrong — over SGD 200,000/night. Double-check this.";

  if (form.rating !== "") {
    const rating = Number(form.rating);
    if (Number.isNaN(rating)) errors.rating = "Rating must be a number.";
    else if (rating < 0 || rating > 5)
      errors.rating = "Rating must be between 0 and 5.";
  }

  if (!form.roomType.trim()) errors.roomType = "Room type is required.";

  if (!form.image.trim()) errors.image = "A main photo is required.";

  const description = form.description.trim();
  if (!description) errors.description = "Description is required.";
  else if (description.length > 2000)
    errors.description = "Description must be under 2000 characters.";

  return errors;
}

async function downloadImage(url, filename = "image.jpg") {
  try {
    const response = await fetch(url, { mode: "cors" });
    if (!response.ok) throw new Error("Failed to fetch image");
    const blob = await response.blob();
    const blobUrl = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = blobUrl;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(blobUrl);
  } catch (err) {
    console.error("Download failed, opening image in a new tab instead:", err);
    window.open(url, "_blank", "noopener,noreferrer");
  }
}

function filenameFromUrl(url, fallback) {
  try {
    const path = decodeURIComponent(new URL(url).pathname);
    const last = path.split("/").pop();
    return last || fallback;
  } catch {
    return fallback;
  }
}
const emptyForm = {
  title: "",
  destination: "", // Area — used for grouping
  price: "", // base "from SGD X" shown on listing cards — actual charges come from pricing_matrix below
  rating: "",
  roomType: "",
  bed_type: "King Bed", // shown to the golfer as "Single Room · King Bed"
  image: "",
  description: "",
  amenities: [],
  // Pricing plan: matrix pricing grid (day-type × night-count), priced per
  // single room. The golfer picks how many rooms to book — twin share is no
  // longer offered.
  holiday_dates: [], // string[] 'yyyy-MM-dd' — just marks which dates are holidays
  pricing_matrix: {}, // { [dayType]: { single_share: {n1,n2plus} } }
};

  function stripUndefined(value) {
    if (Array.isArray(value)) return value.map(stripUndefined);
    if (value && typeof value === "object") {
      return Object.fromEntries(
        Object.entries(value)
          .filter(([, v]) => v !== undefined)
          .map(([k, v]) => [k, stripUndefined(v)]),
      );
    }
    return value;
  }

/** Maps a Firestore package doc back into the form shape. Numbers come back
 * as numbers from Firestore but numeric inputs are controlled strings, so
 * they get stringified here. */
function packageToForm(pkg) {
  return {
    title: pkg.title ?? "",
    destination: pkg.destination ?? "",
    price: pkg.price != null ? String(pkg.price) : "",
    rating: pkg.rating != null ? String(pkg.rating) : "",
    roomType: pkg.roomType ?? "",
    bed_type: pkg.bed_type ?? "King Bed",
    image: pkg.image ?? "",
    description: pkg.description ?? "",
    amenities: Array.isArray(pkg.amenities) ? pkg.amenities : [],
    holiday_dates: Array.isArray(pkg.holiday_dates)
      ? pkg.holiday_dates
      : // Legacy docs from the per-date-price model — keep the dates, the
        // old per-date prices don't carry over (fill in the matrix's
        // holiday_am/pm cells directly).
        (Array.isArray(pkg.public_holiday_dates)
          ? pkg.public_holiday_dates
          : Array.isArray(pkg.public_holidays)
            ? pkg.public_holidays.map((h) => h.date)
            : []),
    pricing_matrix:
      pkg.pricing_matrix && typeof pkg.pricing_matrix === "object"
        ? pkg.pricing_matrix
        : {},
  };
}

/** Derives the 1/2/3 tier badge from price/night. Adjust the thresholds
 * below to match whatever bands the live booking widget actually uses. */
function deriveTierFromPrice(price) {
  if (!Number.isFinite(price)) return null;
  if (price < 100) return 1;
  if (price < 250) return 2;
  return 3;
}

/**
 * @param {{
 *   onSubmit: (payload: any) => Promise<any>;                      // create
 *   onUpdate: (packageId: string, payload: any) => Promise<any>;   // edit
 *   onDelete: (packageId: string, imageUrl?: string) => Promise<any>;
 *   onFetchFerry: () => Promise<Array<any>>;
 *   onCancel?: () => void;
 *   onUploadImage: (file: File) => Promise<string>; // uploads to storage (GCS/Firebase), resolves to a public URL
 * }} props
 */
export default function CreateAccommodationAdmin({
  onSubmit,
  onUpdate,
  onDelete,
  onFetchFerry,
  onCancel,
  onUploadImage,
}) {
  // Manage is the default landing view — see create-course.jsx for why.
  const [view, setView] = useState("manage"); // "form" | "manage"
  const [editingId, setEditingId] = useState(null);

  const [form, setForm] = useState(emptyForm);
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  const [newAmenities, setNewAmenities] = useState("");

  const [packages, setFerry] = useState([]);
  const [loadingFerry, setLoadingFerry] = useState(false);
  const [packagesError, setFerryError] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const [duplicatingId, setDuplicatingId] = useState(null);
  

  const fetchFerry = useCallback(async () => {
    setLoadingFerry(true);
    setFerryError(null);
    try {
      const list = await onFetchFerry();
      setFerry(list || []);
    } catch (err) {
      console.error("Failed to load packages:", err);
      setFerryError(err?.message || "Failed to load packages.");
    } finally {
      setLoadingFerry(false);
    }
  }, [onFetchFerry]);

  useEffect(() => {
    if (view === "manage") fetchFerry();
  }, [view, fetchFerry]);

  const setField = (field, value) =>
    setForm((prev) => ({ ...prev, [field]: value }));
  const markTouched = (field) =>
    setTouched((prev) => ({ ...prev, [field]: true }));

  const addToList = (field, value, setValue) => {
    const trimmed = value.trim();
    if (!trimmed) return;
    setField(field, [...form[field], trimmed]);
    setValue("");
  };
  const removeFromList = (field, index) =>
    setField(
      field,
      form[field].filter((_, i) => i !== index),
    );

  const resetForm = () => {
    setForm(emptyForm);
    setErrors({});
    setTouched({});
    setNewAmenities("");
    setSubmitError(null);
    setSubmitSuccess(false);
  };

  // Public holiday calendar — just marks which dates are holidays; the
  // PRICE for those dates comes from the matrix's holiday_am/holiday_pm
  // columns (flat for every holiday date), not a per-date price here.
  const [newHolidayDate, setNewHolidayDate] = useState(
    /** @type {Date | undefined} */ (undefined),
  );
  const [holidayDatePickerOpen, setHolidayDatePickerOpen] = useState(false);

  const addHolidayDate = (date) => {
    if (!date) return;
    const dateStr = format(date, "yyyy-MM-dd");
    if (!form.holiday_dates.includes(dateStr)) {
      setField("holiday_dates", [...form.holiday_dates, dateStr].sort());
    }
    setNewHolidayDate(undefined);
  };

  const removeHolidayDate = (date) => {
    setField(
      "holiday_dates",
      form.holiday_dates.filter((d) => d !== date),
    );
  };

  const startCreate = () => {
    setEditingId(null);
    resetForm();
    setView("form");
  };

  const startEdit = (pkg) => {
    setEditingId(pkg.id);
    setForm(packageToForm(pkg));
    setErrors({});
    setTouched({});
    setSubmitError(null);
    setSubmitSuccess(false);
    setView("form");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError(null);
    setSubmitSuccess(false);

    const validationErrors = validateAccommodationForm(form);
    setErrors(validationErrors);
    setTouched(
      Object.keys(form).reduce((acc, key) => ({ ...acc, [key]: true }), {}),
    );

    if (Object.keys(validationErrors).length > 0) return;

    const price = Number(form.price);

    const payload = {
      title: form.title.trim(),
      destination: form.destination.trim(), // Area
      price,
      tier: deriveTierFromPrice(price), // auto-derived, not hand-entered
      rating: form.rating !== "" ? Number(form.rating) : null,
      roomType: form.roomType.trim(),
      bed_type: form.bed_type.trim() || "King Bed",
      image: form.image.trim(),
      description: form.description.trim(),
      amenities: form.amenities.map((i) => i.trim()).filter(Boolean),
      holiday_dates: form.holiday_dates,
      pricing_matrix: form.pricing_matrix,
    };

    setIsSubmitting(true);
    try {
      if (editingId) {
        await onUpdate(editingId, payload);
      } else {
        await onSubmit(payload);
      }
      setSubmitSuccess(true);
      if (!editingId) resetForm();
    } catch (err) {
      console.error("Failed to save package:", err);
      setSubmitError(
        err?.message || "Failed to save package. Please try again.",
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDuplicate = async (accommodation) => {
    setFerryError(null);
    setDuplicatingId(accommodation.id);
    try {
      const { id, createdAt, updatedAt, ...rest } = accommodation;
      const payload = stripUndefined({
        ...rest,
        destination: `${accommodation.destination} (Copy)`,
      });
      await onSubmit(payload);
    } catch (err) {
      console.error("Failed to duplicate accommodation:", err);
      setFerryError(
        err?.message || "Failed to duplicate accommodation. Please try again.",
      );
    } finally {
      setDuplicatingId(null);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setIsDeleting(true);
    try {
      await onDelete(deleteTarget.id, deleteTarget.image);
      setFerry((prev) => prev.filter((p) => p.id !== deleteTarget.id));
      setDeleteTarget(null);
    } catch (err) {
      console.error("Failed to delete package:", err);
      setFerryError(err?.message || "Failed to delete package.");
    } finally {
      setIsDeleting(false);
    }
  };

  const fieldError = (field) =>
    touched[field] && errors[field] ? errors[field] : null;

  return (
    <div className="min-h-screen bg-[#F7F8F7] py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="mb-8"
        >
          <div className="flex items-center justify-between w-full mb-2">
            <div className="flex items-center gap-3">
              {view === "form" && editingId && (
                <button
                  type="button"
                  onClick={() => setView("manage")}
                  className="h-9 w-9 rounded-lg border border-gray-200 flex items-center justify-center text-gray-500 hover:bg-gray-50"
                >
                  <ArrowLeft className="h-4 w-4" />
                </button>
              )}
              <h1 className="text-3xl font-bold text-[#0B2421]">
                {view === "manage"
                  ? "Manage Accommodation"
                  : editingId
                    ? "Edit Accommodation"
                    : "Add an Accommodation"}
              </h1>
            </div>

            {view === "form" ? (
              <button
                type="button"
                onClick={() => setView("manage")}
                className="h-12 px-6 rounded-xl border border-[#0B2421]/20 text-[#0B2421] font-semibold hover:bg-[#0B2421]/5 transition-colors"
              >
                Manage Accommodation
              </button>
            ) : (
              <button
                type="button"
                onClick={startCreate}
                className="h-12 px-6 rounded-xl bg-[#0B2421] hover:bg-[#0B2421]/85 text-white font-semibold transition-colors flex items-center gap-2"
              >
                <PackagePlus className="h-4 w-4" />
                New Accommodation
              </button>
            )}
          </div>

          <p className="text-gray-500 mt-1">
            {view === "manage"
              ? "Edit or remove existing properties."
              : "Fields marked with an asterisk are required before this can go live."}
          </p>
        </motion.div>

        {view === "manage" ? (
          <ManageView
            packages={packages}
            loading={loadingFerry}
            error={packagesError}
            onEdit={startEdit}
            onDuplicate={handleDuplicate}
            duplicatingId={duplicatingId}
            onDeleteRequest={setDeleteTarget}
            deleteTarget={deleteTarget}
            onDeleteCancel={() => setDeleteTarget(null)}
            onDeleteConfirm={confirmDelete}
            isDeleting={isDeleting}
          />
        ) : (
          <>
            {submitSuccess && (
              <div className="mb-6 flex items-center gap-2 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-emerald-800">
                {editingId
                  ? "Accommodation updated successfully."
                  : "Accommodation created successfully."}
              </div>
            )}
            {submitError && (
              <div className="mb-6 flex items-center gap-2 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-red-700">
                <AlertCircle className="h-4 w-4 flex-shrink-0" />
                <span>{submitError}</span>
              </div>
            )}

            <form onSubmit={handleSubmit} noValidate>
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-start">
                <div className="lg:col-span-2 space-y-6">
                  <Section title="Main Photo" icon={ImageIcon}>
                    <FieldLabel required>Photo</FieldLabel>
                    <MainImageUpload
                      value={form.image}
                      onChange={(url) => setField("image", url)}
                      onBlur={() => markTouched("image")}
                      onUploadImage={onUploadImage}
                      error={fieldError("image")}
                    />
                  </Section>

                  <Section title="Basics">
                    <div className="space-y-4">
                      <Field
                        label="Hotel/Resort Name"
                        required
                        error={fieldError("title")}
                      >
                        <input
                          type="text"
                          value={form.title}
                          onChange={(e) => setField("title", e.target.value)}
                          onBlur={() => markTouched("title")}
                          placeholder="e.g. Turi Beach Resort"
                          className={inputClass(fieldError("title"))}
                        />
                      </Field>

                      <div className="flex gap-4">
                        <div className="flex-1">
                          <Field
                            label="Area"
                            required
                            icon={MapPin}
                            error={fieldError("destination")}
                          >
                            <input
                              type="text"
                              value={form.destination}
                              onChange={(e) =>
                                setField("destination", e.target.value)
                              }
                              onBlur={() => markTouched("destination")}
                              placeholder="e.g. Sekupang, Nongsa"
                              className={inputClass(fieldError("destination"))}
                            />
                          </Field>
                          <p className="mt-1 text-xs text-gray-400">
                            Used for grouping properties on the destination
                            page.
                          </p>
                        </div>

                        <div className="flex-1">
                          <Field
                            label="Room Type"
                            required
                            icon={BedDouble}
                            error={fieldError("roomType")}
                          >
                            <input
                              type="text"
                              value={form.roomType}
                              onChange={(e) =>
                                setField("roomType", e.target.value)
                              }
                              onBlur={() => markTouched("roomType")}
                              placeholder="e.g. Deluxe Sea View"
                              className={inputClass(fieldError("roomType"))}
                            />
                          </Field>
                          <div className="mt-4">
                            <Field label="Bed Type" icon={BedDouble}>
                              <input
                                type="text"
                                value={form.bed_type}
                                onChange={(e) =>
                                  setField("bed_type", e.target.value)
                                }
                                placeholder="e.g. King Bed"
                                className={inputClass(null)}
                              />
                            </Field>
                            <p className="mt-1 text-xs text-gray-400">
                              Shown to golfers as "Single Room · Bed Type".
                            </p>
                          </div>
                        </div>
                      </div>



                      <div className="flex gap-4">
                        <div className="flex-1">
                          <Field
                            label="Price per night"
                            required
                            error={fieldError("price")}
                          >
                            <div className="relative">
                              <span className="absolute left-4 top-1/2 -translate-y-1/2 text-[#D4AF37] font-semibold">
                                SGD
                              </span>
                              <input
                                type="number"
                                min="0"
                                step="0.01"
                                value={form.price}
                                onChange={(e) =>
                                  setField("price", e.target.value)
                                }
                                onBlur={() => markTouched("price")}
                                placeholder="150"
                                className={`${inputClass(fieldError("price"))} pl-16`}
                              />
                            </div>
                          </Field>
                          {form.price !== "" && !fieldError("price") && (
                            <p className="mt-1 text-xs text-gray-400">
                              Tier {deriveTierFromPrice(Number(form.price))}{" "}
                              badge will be applied automatically.
                            </p>
                          )}
                        </div>

                        <div className="flex-1">
                          <Field
                            label="Rating (0–5)"
                            icon={Star}
                            error={fieldError("rating")}
                          >
                            <input
                              type="number"
                              min="0"
                              max="5"
                              step="0.1"
                              value={form.rating}
                              onChange={(e) =>
                                setField("rating", e.target.value)
                              }
                              onBlur={() => markTouched("rating")}
                              placeholder="4.5"
                              className={inputClass(fieldError("rating"))}
                            />
                          </Field>
                        </div>
                      </div>

                      <Field
                        label="Description"
                        required
                        error={fieldError("description")}
                      >
                        <textarea
                          value={form.description}
                          onChange={(e) =>
                            setField("description", e.target.value)
                          }
                          onBlur={() => markTouched("description")}
                          rows={4}
                          placeholder="What makes this property worth booking?"
                          className={`${inputClass(fieldError("description"))} h-auto py-3 resize-none`}
                        />
                        <div className="flex justify-between mt-1">
                          <span className="text-xs text-gray-400">
                            Shown under the heading "About this property".
                          </span>
                          <span className="text-xs text-gray-400">
                            {form.description.length}/2000
                          </span>
                        </div>
                      </Field>
                    </div>
                  </Section>

                  <Section title="Public Holiday Dates" icon={CalendarIcon}>
                    <p className="text-sm text-gray-500 mb-3">
                      Mark which dates are public holidays — the price for
                      those dates is set in the Pricing grid below (the
                      "Public Holiday AM/PM" tabs), applied to every date
                      listed here.
                    </p>
                    <div className="flex gap-2 mb-3">
                      <Popover
                        open={holidayDatePickerOpen}
                        onOpenChange={setHolidayDatePickerOpen}
                      >
                        <PopoverTrigger asChild>
                          <button
                            type="button"
                            className="flex-1 h-11 rounded-xl border border-gray-200 bg-gray-50 px-4 flex items-center gap-2 text-left text-gray-700 hover:border-[#0B2421] transition-colors"
                          >
                            <CalendarIcon className="h-4 w-4 text-[#D4AF37] shrink-0" />
                            <span
                              className={
                                newHolidayDate ? "text-gray-700" : "text-gray-400"
                              }
                            >
                              {newHolidayDate
                                ? format(newHolidayDate, "d MMM yyyy")
                                : "Pick a public holiday date"}
                            </span>
                          </button>
                        </PopoverTrigger>
                        <PopoverContent
                          className="w-auto p-0"
                          align="start"
                          sideOffset={8}
                        >
                          <Calendar
                            mode="single"
                            selected={newHolidayDate}
                            onSelect={(d) => addHolidayDate(d)}
                          />
                        </PopoverContent>
                      </Popover>
                    </div>

                    {form.holiday_dates.length > 0 ? (
                      <div className="flex flex-wrap gap-2">
                        {form.holiday_dates.map((date) => (
                          <span
                            key={date}
                            className="inline-flex items-center gap-1.5 rounded-full bg-amber-50 border border-amber-100 px-3 py-1.5 text-sm text-amber-700"
                          >
                            {format(new Date(`${date}T00:00:00`), "d MMM yyyy")}
                            <button
                              type="button"
                              onClick={() => removeHolidayDate(date)}
                              className="text-amber-400 hover:text-amber-600 transition-colors"
                            >
                              <X className="h-3 w-3" />
                            </button>
                          </span>
                        ))}
                      </div>
                    ) : (
                      <p className="text-xs text-gray-400">
                        No public holiday dates set yet.
                      </p>
                    )}
                  </Section>

                  <Section title="Nightly Pricing">
                    <p className="text-sm text-gray-500 mb-4">
                      Per-night price for one single room, by day type and
                      total nights in the stay. The golfer picks how many
                      rooms to book (one per player by default) and is
                      charged this rate × rooms × nights. The same rate
                      applies to every night once the total night count picks
                      a column.
                    </p>
                   <MatrixPriceEditor
                      dayTypes={ACCOMMODATION_DAY_TYPES}
                      rows={[
                        { key: "single_share", label: "Single Room (per room)" },
                      ]}
                      columns={[
                        { key: "n1", label: "1 Night" },
                        { key: "n2plus", label: "2 or more Nights" },
                      ]}
                      value={form.pricing_matrix}
                      onChange={(m) => setField("pricing_matrix", m)}
                    />
                  </Section>

                  <Section title="Amenities" icon={CheckCircle2}>
                    <ChipListEditor
                      items={form.amenities}
                      inputValue={newAmenities}
                      setInputValue={setNewAmenities}
                      onAdd={() =>
                        addToList("amenities", newAmenities, setNewAmenities)
                      }
                      onRemove={(i) => removeFromList("amenities", i)}
                      placeholder="e.g., Free WiFi"
                    />
                    <p className="mt-2 text-xs text-gray-400">
                      Rendered as a checklist under the "Amenities" heading on
                      the live listing.
                    </p>
                  </Section>

                  <div className="flex items-center gap-3 justify-end pb-4">
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="h-12 px-8 rounded-xl bg-[#0B2421] hover:bg-[#0B2421]/85 text-white font-semibold transition-colors disabled:opacity-40 flex items-center gap-2"
                    >
                      {isSubmitting ? (
                        <>
                          <Loader2 className="h-4 w-4 animate-spin" />
                          {editingId ? "Saving..." : "Creating..."}
                        </>
                      ) : editingId ? (
                        "Save Changes"
                      ) : (
                        "Create Accommodation"
                      )}
                    </button>
                  </div>
                </div>

                {/* Live preview */}
                <div className="hidden lg:block lg:sticky lg:top-8">
                  <LivePreview form={form} />
                </div>
              </div>
            </form>
          </>
        )}
      </div>
    </div>
  );
}

/* ---------- Manage / list view ---------- */

function ManageView({
  packages,
  loading,
  error,
  onEdit,
  onDuplicate,
  duplicatingId,
  onDeleteRequest,
  deleteTarget,
  onDeleteCancel,
  onDeleteConfirm,
  isDeleting,
}) {
  const {
    items: orderedPackages,
    getDragHandlers,
    isDragging,
    isDragOver,
  } = useDragReorder(
    packages,
    (p) => p.id,
    (ids) => saveItemOrder("accommodation", ids),
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24 text-gray-400 gap-2">
        <Loader2 className="h-5 w-5 animate-spin" />
        Loading properties...
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center gap-2 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-red-700">
        <AlertCircle className="h-4 w-4 flex-shrink-0" />
        <span>{error}</span>
      </div>
    );
  }

  if (packages.length === 0) {
    return (
      <div className="rounded-2xl border border-dashed border-gray-200 bg-white p-12 text-center">
        <PackagePlus className="h-8 w-8 text-gray-300 mx-auto mb-3" />
        <p className="text-gray-500">No properties yet.</p>
      </div>
    );
  }

  return (
    <>
      <p className="text-xs text-gray-400 mb-3 flex items-center gap-1.5">
        <GripVertical className="h-3.5 w-3.5" />
        Drag a card to reorder — this is the order shown to golfers.
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {orderedPackages.map((pkg) => (
          <div
            key={pkg.id}
            {...getDragHandlers(pkg)}
            className={`rounded-2xl border bg-white shadow-sm overflow-hidden group cursor-grab active:cursor-grabbing transition-opacity ${
              isDragging(pkg)
                ? "opacity-40 border-gray-100"
                : isDragOver(pkg)
                  ? "border-[#0B2421] ring-2 ring-[#0B2421]/30"
                  : "border-gray-100"
            }`}
          >
            <div className="relative h-36 bg-gray-100">
              <div className="absolute top-2 left-2 z-10 h-6 w-6 rounded-md bg-black/40 backdrop-blur-sm flex items-center justify-center text-white/80">
                <GripVertical className="h-3.5 w-3.5" />
              </div>
              {pkg.image ? (
                <img
                  src={pkg.image}
                  alt=""
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-gray-300">
                  <ImageIcon className="h-6 w-6" />
                </div>
              )}
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors flex items-center justify-center gap-2 opacity-0 group-hover:opacity-100">
                <button
                  type="button"
                  onClick={() => onEdit(pkg)}
                  className="h-9 px-4 rounded-lg bg-white text-[#0B2421] text-sm font-semibold flex items-center gap-1.5"
                >
                  <Pencil className="h-3.5 w-3.5" />
                  Edit
                </button>

                
                <button
                  type="button"
                  onClick={() => onDuplicate(pkg)}
                  disabled={duplicatingId === pkg.id}
                  className="h-9 px-4 rounded-lg bg-white text-[#0B2421] text-sm font-semibold flex items-center gap-1.5 disabled:opacity-40"
                >
                  {duplicatingId === pkg.id ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  ) : (
                    <Copy className="h-3.5 w-3.5" />
                  )}
                  Duplicate
                </button>


                <button
                  type="button"
                  onClick={() => onDeleteRequest(pkg)}
                  className="h-9 px-4 rounded-lg bg-white/90 text-red-600 text-sm font-semibold flex items-center gap-1.5"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                  Delete
                </button>
              </div>
            </div>
            <div className="p-4">
              {pkg.destination && (
                <p className="text-[10px] font-semibold uppercase tracking-widest text-[#D4AF37] mb-0.5">
                  {pkg.destination}
                </p>
              )}
              <h3 className="text-sm font-bold text-[#0B2421] truncate">
                {pkg.title}
              </h3>
              <div className="flex items-center justify-between mt-2">
                <span className="text-xs text-gray-500 truncate pr-2">
                  {pkg.roomType}
                </span>
                {pkg.price != null && (
                  <span className="text-sm font-bold text-[#0B2421] flex-shrink-0">
                    SGD {pkg.price}
                    <span className="text-xs font-normal text-gray-400">
                      /night
                    </span>
                  </span>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      <AnimatePresence>
        {deleteTarget && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50"
            onClick={onDeleteCancel}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white rounded-2xl p-6 max-w-sm w-full"
            >
              <h3 className="text-lg font-bold text-[#0B2421] mb-2">
                Delete property?
              </h3>
              <p className="text-sm text-gray-500 mb-6">
                This will permanently remove "{deleteTarget.title}" and its main
                photo. This can't be undone.
              </p>
              <div className="flex items-center gap-3 justify-end">
                <button
                  type="button"
                  onClick={onDeleteCancel}
                  disabled={isDeleting}
                  className="h-10 px-4 rounded-xl border border-gray-200 text-gray-600 font-medium hover:bg-gray-50 disabled:opacity-40"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={onDeleteConfirm}
                  disabled={isDeleting}
                  className="h-10 px-4 rounded-xl bg-red-600 hover:bg-red-700 text-white font-semibold disabled:opacity-40 flex items-center gap-2"
                >
                  {isDeleting ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Deleting...
                    </>
                  ) : (
                    "Delete"
                  )}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}

/* ---------- layout primitives (shared visual language with CreateCourseAdmin) ---------- */

function Section({ title, icon: Icon, children }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      className="p-6 bg-white border border-gray-100 rounded-2xl shadow-sm"
    >
      <h2 className="text-lg font-bold mb-4 text-[#0B2421] flex items-center">
        {Icon && <Icon className="h-5 w-5 mr-2" />}
        {title}
      </h2>
      {children}
    </motion.div>
  );
}

function FieldLabel({ children, required, icon: Icon }) {
  return (
    <label className="text-sm font-semibold text-[#0B2421] mb-2 block">
      {Icon && <Icon className="inline h-4 w-4 mr-1" />}
      {children} {required && <span className="text-[#D4AF37]">*</span>}
    </label>
  );
}

function Field({ label, required, icon, error, children }) {
  return (
    <div>
      <FieldLabel required={required} icon={icon}>
        {label}
      </FieldLabel>
      {children}
      {error && <FieldError message={error} />}
    </div>
  );
}

function inputClass(hasError) {
  return `w-full h-12 rounded-xl border px-4 text-gray-700 focus:outline-none focus:ring-2 focus:ring-[#0B2421]/30 ${
    hasError ? "border-red-300 bg-red-50" : "border-gray-200 bg-gray-50"
  }`;
}

function FieldError({ message }) {
  return (
    <p className="mt-1 text-xs text-red-600 flex items-center gap-1">
      <AlertCircle className="h-3 w-3 flex-shrink-0" />
      {message}
    </p>
  );
}

/* ---------- image upload (identical UX to CreateCourseAdmin) ---------- */

function MainImageUpload({ value, onChange, onBlur, onUploadImage, error }) {
  const [isDragging, setIsDragging] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [localError, setLocalError] = useState(null);
  const inputRef = useRef(null);

  const handleFile = useCallback(
    async (file) => {
      if (!file || !isImageFile(file)) {
        setLocalError("Please choose an image file.");
        return;
      }
      setLocalError(null);
      setIsUploading(true);
      try {
        const url = await onUploadImage(file);
        onChange(url);
      } catch (err) {
        console.error("Image upload failed:", err);
        setLocalError(err?.message || "Upload failed. Try again.");
      } finally {
        setIsUploading(false);
        onBlur?.();
      }
    },
    [onUploadImage, onChange, onBlur],
  );

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    handleFile(e.dataTransfer.files?.[0]);
  };

  if (value && !isUploading) {
    return (
      <div className="relative group rounded-xl overflow-hidden border border-gray-200 h-56">
        <img
          src={value}
          alt="Property main photo preview"
          className="w-full h-full object-cover"
        />

        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors flex items-center justify-center gap-2 opacity-0 group-hover:opacity-100">
          <button
            type="button"
            onClick={() =>
              downloadImage(value, filenameFromUrl(value, "main-photo.jpg"))
            }
            className="px-4 py-2 rounded-lg bg-white/90 text-green-600 text-sm font-semibold"
          >
            Download
          </button>
          
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            className="px-4 py-2 rounded-lg bg-white text-[#0B2421] text-sm font-semibold"
          >
            Replace
          </button>
          <button
            type="button"
            onClick={() => onChange("")}
            className="px-4 py-2 rounded-lg bg-white/90 text-red-600 text-sm font-semibold"
          >
            Remove
          </button>
        </div>
        <input
          ref={inputRef}
          type="file"
          accept={IMAGE_ACCEPT}
          className="hidden"
          onChange={(e) => handleFile(e.target.files?.[0])}
        />
      </div>
    );
  }

  return (
    <div>
      <label
        onDragOver={(e) => {
          e.preventDefault();
          setIsDragging(true);
        }}
        onDragLeave={() => setIsDragging(false)}
        onDrop={handleDrop}
        className={`flex flex-col items-center justify-center h-56 rounded-xl border-2 border-dashed cursor-pointer transition-colors ${
          isDragging
            ? "border-[#0B2421] bg-[#0B2421]/5"
            : error || localError
              ? "border-red-300 bg-red-50"
              : "border-gray-200 bg-gray-50 hover:bg-gray-100"
        }`}
      >
        {isUploading ? (
          <>
            <Loader2 className="h-6 w-6 text-[#0B2421] animate-spin mb-2" />
            <span className="text-sm text-gray-500">Uploading...</span>
          </>
        ) : (
          <>
            <UploadCloud className="h-6 w-6 text-gray-400 mb-2" />
            <span className="text-sm font-medium text-gray-600">
              Click to upload or drag a photo here
            </span>
            <span className="text-xs text-gray-400 mt-1">
              This shows as the hero banner on the listing page
            </span>
          </>
        )}
        <input
          type="file"
          accept={IMAGE_ACCEPT}
          className="hidden"
          onChange={(e) => handleFile(e.target.files?.[0])}
        />
      </label>
      {(error || localError) && <FieldError message={error || localError} />}
    </div>
  );
}

/* ---------- chip list (identical to CreateCourseAdmin) ---------- */

function ChipListEditor({
  items,
  inputValue,
  setInputValue,
  onAdd,
  onRemove,
  placeholder,
}) {
  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      onAdd();
    }
  };

  return (
    <div>
      <div className="flex gap-2 mb-3">
        <input
          type="text"
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          className="flex-1 h-11 rounded-xl border border-gray-200 bg-gray-50 px-4 text-gray-700 focus:outline-none focus:ring-2 focus:ring-[#0B2421]/30"
        />
        <button
          type="button"
          onClick={onAdd}
          className="h-11 w-11 rounded-xl bg-[#0B2421] hover:bg-[#0B2421]/85 text-white flex items-center justify-center flex-shrink-0 transition-colors"
        >
          <Plus className="h-4 w-4" />
        </button>
      </div>
      {items.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {items.map((item, index) => (
            <span
              key={`${item}-${index}`}
              className="inline-flex items-center gap-1.5 rounded-full bg-gray-100 px-3 py-1.5 text-sm text-gray-700"
            >
              {item}
              <button
                type="button"
                onClick={() => onRemove(index)}
                className="text-gray-400 hover:text-red-500 transition-colors"
              >
                <X className="h-3 w-3" />
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

/* ---------- live preview — mirrors the real listing hero + details card ---------- */

function LivePreview({ form }) {
  const hasContent = form.title || form.image || form.destination;

  return (
    <div className="rounded-2xl border border-gray-100 bg-white shadow-sm overflow-hidden">
      <div className="px-5 py-4 border-b border-gray-100 bg-[#0B2421]">
        <span className="text-xs font-semibold tracking-wide text-[#D4AF37] uppercase">
          Preview
        </span>
      </div>

      {!hasContent ? (
        <div className="p-8 text-center text-sm text-gray-400">
          Fill in the form to see how this property will appear to golfers.
        </div>
      ) : (
        <div>
          <div className="relative h-40 bg-gray-100">
            {form.image ? (
              <img
                src={form.image}
                alt=""
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-gray-300">
                <ImageIcon className="h-8 w-8" />
              </div>
            )}
            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent" />
            <div className="absolute bottom-3 left-4 right-4">
              {form.destination && (
                <p className="text-[#D4AF37] text-[10px] font-semibold uppercase tracking-widest mb-0.5">
                  {form.destination}
                </p>
              )}
              <h3 className="text-white font-bold text-base leading-snug">
                {form.title || "Hotel/Resort name"}
              </h3>
            </div>
          </div>

          <div className="p-5">
            <div className="flex items-center justify-between mb-1">
              {form.roomType && (
                <p className="text-sm text-gray-500">{form.roomType}</p>
              )}
              {form.rating !== "" && (
                <div className="flex items-center gap-1 text-sm text-[#0B2421] font-semibold">
                  <Star className="h-3.5 w-3.5 fill-[#D4AF37] text-[#D4AF37]" />
                  {form.rating}
                </div>
              )}
            </div>

            {form.price && (
              <div className="flex items-baseline gap-1 mb-1">
                <span className="text-[#0B2421] font-bold text-2xl">
                  SGD {form.price}
                </span>
                <span className="text-gray-400 text-sm">/ night</span>
                <span className="ml-2 text-[10px] font-semibold uppercase tracking-wide text-[#D4AF37] border border-[#D4AF37]/30 rounded-full px-2 py-0.5">
                  Tier {deriveTierFromPrice(Number(form.price))}
                </span>
              </div>
            )}

            {form.description && (
              <div className="mt-3">
                <p className="text-xs font-semibold text-[#0B2421] mb-1">
                  About this property
                </p>
                <p className="text-xs text-gray-500 line-clamp-3">
                  {form.description}
                </p>
              </div>
            )}

            {form.amenities.length > 0 && (
              <div className="mt-4">
                <p className="text-xs font-semibold text-[#0B2421] mb-1.5">
                  Amenities
                </p>
                <div className="space-y-1.5">
                  {form.amenities.slice(0, 4).map((item, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <CheckCircle2 className="h-3.5 w-3.5 text-[#D4AF37] flex-shrink-0" />
                      <span className="text-xs text-gray-600">{item}</span>
                    </div>
                  ))}
                  {form.amenities.length > 4 && (
                    <span className="text-xs text-gray-400 pl-5">
                      +{form.amenities.length - 4} more
                    </span>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}